import { useMutation } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { PinLoginRequest } from '@/api/handlers/resources/auth/requests';
import type { PinVerifyResponse } from '@/api/handlers/resources/auth/responses';
import type { ApiResponse } from '@/api/handlers/responses';

/**
 * PIN pre-check for the shift discrepancy co-sign. Calls the device-gated
 * `POST /auth/pin/verify`, which confirms the approver's PIN through the same
 * lockout-aware pipeline as sign-in but mints no session — so the current
 * seller's shift session is left untouched (a co-sign is a second person, not a
 * re-login). The server returns 200 on a valid PIN and an `ApiError` on failure
 * (`PIN_LOCKED`, a 429 `RATE_LIMITED`, or a generic 400), so callers branch on
 * `result.success` and classify the failure with `usePinAttemptError`.
 *
 * This is only a UX pre-check that enables the start button; `POST /shifts/open`
 * and `/shifts/close` re-verify the co-sign server-side (the authoritative gate).
 */
export const useVerifyPinMutation = () => {
    const { apiClient } = useApi();

    return useMutation<ApiResponse<PinVerifyResponse>, Error, PinLoginRequest>({
        mutationFn: (body) => apiClient.auth.verifyPin(body),
    });
};
